import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { MessageModal } from '../../components/modals/MessageModal';
export function SignInPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate login success with 2FA check
    setModalContent({
      title: 'Login Successful',
      message: 'Redirecting to 2FA verification...',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      navigate('/auth/verify-2fa', {
        state: {
          email: formData.email
        }
      });
    }, 1500);
  };
  const handleSocialLogin = (provider: string) => {
    setModalContent({
      title: `${provider} Login`,
      message: `Redirecting to ${provider} authentication... In production, this would use OAuth 2.0 flow.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-2241">
      {/* Header */}
      <header className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-2242">
        <Link to="/" className="flex items-center gap-2" data-id="element-2243">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-2244"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-2245">
            Citiplots.com
          </span>
        </Link>
      </header>

      <main className="max-w-md mx-auto px-4 py-12" data-id="element-2246">
        <div className="bg-white border border-gray-300 p-8 shadow-sm" data-id="element-2247">
          <h1 className="text-2xl font-bold mb-2" data-id="element-2248">Welcome Back</h1>
          <p className="text-sm text-gray-500 mb-6" data-id="element-2249">
            Sign in to your Citiplots account
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2250">
            {/* Email */}
            <div data-id="element-2251">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2252">
                Email Address
              </label>
              <input type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2253" />
            </div>

            {/* Password */}
            <div data-id="element-2254">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2255">
                Password
              </label>
              <div className="relative" data-id="element-2256">
                <input type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2257" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2258">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2259" /> : <Eye className="w-4 h-4" data-id="element-2260" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between" data-id="element-2261">
              <label className="flex items-center gap-2" data-id="element-2262">
                <input type="checkbox" checked={formData.rememberMe} onChange={e => setFormData({
                ...formData,
                rememberMe: e.target.checked
              })} data-id="element-2263" />
                <span className="text-xs text-gray-600" data-id="element-2264">Remember me</span>
              </label>
              <Link to="/auth/forgot-password" className="text-xs text-blue-600 hover:underline" data-id="element-2265">
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button type="submit" className="w-full py-3 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-2266">
              Sign In
            </button>
          </form>

          {/* Social Login */}
          <div className="mt-6" data-id="element-2267">
            <div className="relative" data-id="element-2268">
              <div className="absolute inset-0 flex items-center" data-id="element-2269">
                <div className="w-full border-t border-gray-300" data-id="element-2270"></div>
              </div>
              <div className="relative flex justify-center text-xs" data-id="element-2271">
                <span className="bg-white px-2 text-gray-500" data-id="element-2272">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2" data-id="element-2273">
              {['Google', 'LinkedIn', 'Facebook'].map(provider => <button key={provider} type="button" onClick={() => handleSocialLogin(provider)} className="py-2 border border-gray-300 text-xs font-medium hover:bg-gray-50" data-id="element-2274">
                  {provider}
                </button>)}
            </div>
          </div>

          {/* Sign Up Link */}
          <p className="mt-6 text-center text-sm text-gray-600" data-id="element-2275">
            Don't have an account?{' '}
            <Link to="/auth/signup" className="text-blue-600 font-bold hover:underline" data-id="element-2276">
              Sign Up
            </Link>
          </p>
        </div>
      </main>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-2277" />
    </div>;
}